class PaymentInstallmentViewController extends BaseTableViewController implements InstallmentCellDelegate, PaymentSessionTransactionsViewControllerDelegate
{
    static newInstance() : PaymentInstallmentViewController
    {
        let vc = new PaymentInstallmentViewController( "payment-installments-view" );
        vc.initWithResource( "layout/paymentinstallments/PaymentInstallmentsView.html" );
        return vc;
    }

    private refreshButton:MUIButton = null;
    private calendarButton:MUIButton = null;
    private saveButton:MUIButton = null;
    private settingsButton:MUIButton = null;
    private statusComboBox:MUIComboBox = null;
    private sortComboBox:MUIComboBox = null;
    private dueHeaderTemplate:PaymentInstallmentDueHeader = null;

    private beginDateString:string;
    private endDateString:string

    /// all, review, pending or paid
    private statusFilter = "all";
    /// "dueDate" ( the default: cards by when the payouts pay ) or "date"
    /// ( cards by the session day; the cells then show the due date )
    private sortMode = "dueDate";

    private columnFilterController:ColumnFilterController = null;

    private documentFilterTextField:ColumnFilterTextField = null;
	private dateFilterTextField:ColumnFilterTextField = null;
	private conceptFilterTextField:ColumnFilterTextField = null;
	private amountFilterTextField:ColumnFilterTextField = null;

    /// The issues of the sessions behind the installments held for review, by
    /// session. Loaded as their rows show and kept until a refresh; null when
    /// the load failed ( the row stays red, as held for review ).
    private sessionIssues:{ [sessionID:string]: PaymentSessionIssues } = {};
    private loadingSessions:{ [sessionID:string]: boolean } = {};
    private reloadTimer:MIOTimer = null;

    viewDidLoad(): void {
        super.viewDidLoad();

        this.columnFilterController = new ColumnFilterController();
		this.columnFilterController.initWithDelegate(this);

        this.documentFilterTextField = MUIOutlet(this, "document-cf-tf", "ColumnFilterTextField");
		this.documentFilterTextField.filterController = this.columnFilterController;
		this.documentFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "documentNumber", null, null);

        this.dateFilterTextField = MUIOutlet(this, "date-cf-tf", "ColumnFilterTextField");
		this.dateFilterTextField.filterController = this.columnFilterController;
		this.dateFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Date, "date", null, null);

        this.conceptFilterTextField = MUIOutlet(this, "concept-cf-tf", "ColumnFilterTextField");
		this.conceptFilterTextField.filterController = this.columnFilterController;
		this.conceptFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.String, "concept", null, null);

        this.amountFilterTextField = MUIOutlet(this, "amount-cf-tf", "ColumnFilterTextField");
		this.amountFilterTextField.filterController = this.columnFilterController;
		this.amountFilterTextField.setOnFilterChange(ColumnFilterTextFieldType.Currency, "amount", null, null);

        this.statusComboBox = MUIOutlet( this, "status-cb", "MUIComboBox" );
        this.statusComboBox.setOnChangeAction( this, function(this:PaymentInstallmentViewController, control:MUIComboBox, value:string) {
            this.statusFilter = value;
            this.invalidateFetch();
        });

        this.sortComboBox = MUIOutlet( this, "sort-cb", "MUIComboBox" );
        this.sortComboBox.setOnChangeAction( this, function(this:PaymentInstallmentViewController, control:MUIComboBox, value:string) {
            this.sortMode = value;
            this.invalidateFetch();
        });

        this.refreshButton = MUIOutlet( this, "refresh-btn", "MUIButton" );
        this.refreshButton.setAction( this, function(this:PaymentInstallmentViewController) {
            this.sessionIssues = {};
            this.invalidateFetch();
        });

        this.calendarButton = MUIOutlet(this, 'calendar-btn', 'MUIButton');
        this.calendarButton.setAction(this, function(){
            let ah:AppHelper = AppHelper.sharedInstance();
            ah.presentDateSelectionViewController(this);
        });

        this.saveButton = MUIOutlet( this, "save-btn", "MUIButton" );
        this.saveButton.setAction( this, function(){
            DBHelper.saveMainContext();
        } );

        this.settingsButton = MUIOutlet( this, "settings-btn", "MUIButton" );
        if (this.settingsButton != null) {
            this.settingsButton.setAction( this, function(this:PaymentInstallmentViewController) {
                this.showOptions();
            });
        }

        this.tableView = MUIOutlet( this, "table-view", "UITableView" );
        this.tableView.dataSource = this;
        this.tableView.delegate = this as any;
        this.dueHeaderTemplate = MUIOutlet( this, "due-header-view", "PaymentInstallmentDueHeader" );

        let today = MIODateToday();
        let m = MIODateGetMonthFromDate( today );
        let y = MIODateGetYearFromDate( today );
        let d = MIODateGetLastDayOfTheMonth( m, y );
        let month = ( m + 1 ).toString();
        this.beginDateString = y + "-" + (month[1]?month:"0" + month[0]) + "-01 00:00:00";
        this.endDateString = y + "-" + (month[1]?month:"0" + month[0]) + "-" + d + " 23:59:59";
    }

    datesDidSelect(dateTimeFrom:Date, dateTimeTo:Date) {
        this.beginDateString = MIODateGetString(dateTimeFrom);
        this.endDateString = MIODateGetString(dateTimeTo);
        this.invalidateFetch();
    }

    private installmentsInSection( section:number ) : PaymentInstallment[] {
        return ( this.fetchedResultsController.sections[ section ]?.objects ?? [] ) as PaymentInstallment[];
    }

    /// The rows of a day, with every split chain together: the voided total
    /// ( the root ) first, its parts right below it. A part whose root fell
    /// outside the fetch shows on its own.
    private _arranged:{ [section:number]:PaymentInstallment[] } = {};
    private arrangedInstallmentsInSection( section:number ) : PaymentInstallment[] {
        let cached = this._arranged[ section ];
        if (cached != null) return cached;

        let objects = this.installmentsInSection( section );
        let present = new Set<PaymentInstallment>();
        for (let item of objects) present.add( item );

        let rows:PaymentInstallment[] = [];
        for (let item of objects) {
            if (item.previous != null && present.has( item.previous )) continue;
            rows.push( item );
            let part = item.next;
            while (part != null && present.has( part )) { rows.push( part ); part = part.next; }
        }

        this._arranged[ section ] = rows;
        return rows;
    }

    viewForHeaderInSection(tableView:UITableView, section:number) {
        let installments = this.installmentsInSection( section );

        let header = this.dueHeaderTemplate.copy() as PaymentInstallmentDueHeader;
        header.awakeFromHTML();
        header.hidden = false;
        header.showsDate = this.sortMode == "date";
        header.setInstallments( installments, installments.map( i => this.issuesOfInstallment( i ) ) );

        return header;
    }

    // The count and the cells must read the SAME arranged snapshot: the
    // fetched results can grow between the two while a sync is landing, and
    // a row past the arranged array crashed the cell with an undefined item.
    numberOfRowsInSection(tableView:UITableView, section:number) {
        return this.arrangedInstallmentsInSection( section ).length;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let item = this.arrangedInstallmentsInSection( indexPath.section )[ indexPath.row ];

        let cell = tableView.dequeueReusableCellWithIdentifier( "PaymentInstallmentCell" ) as InstallmentCell;
        cell.delegate = this;
        cell.issues = this.issuesOfInstallment( item );
        cell.showsDueDate = this.sortMode == "date";
        cell.item = item;
        cell.isLastInSection = indexPath.row == this.numberOfRowsInSection( tableView, indexPath.section ) - 1;

        return cell;
    }

    // Only the installments held for review open their session's transactions
    canSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) : boolean {
        let item = this.arrangedInstallmentsInSection( indexPath.section )[ indexPath.row ];
        return InstallmentCell.isReviewable( item );
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let item = this.arrangedInstallmentsInSection( indexPath.section )[ indexPath.row ];
        if (InstallmentCell.isReviewable( item )) this.showSessionTransactions( item as AdyenProxyPaymentInstallment );
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        // Grouped by due date ( the default ) or by the session day
        let by_date = this.sortMode == "date";
        let sortDescriptors = by_date
            ? [ MIOSortDescriptor.sortDescriptorWithKey("date", true), MIOSortDescriptor.sortDescriptorWithKey("dueDate", true) ]
            : [ MIOSortDescriptor.sortDescriptorWithKey("dueDate", true), MIOSortDescriptor.sortDescriptorWithKey("date", true) ];

        // The period applies to the day the cards group by, not always the session day
        let date_key = by_date ? "date" : "dueDate";
        let predicateFormat = date_key + " >= '" + this.beginDateString + "' AND " + date_key + " <= '" + this.endDateString + "'";

        let statusPredicateFormat = this.statusPredicateFormat();
        if (statusPredicateFormat != null) {
            predicateFormat += " AND " + statusPredicateFormat;
        }

        let filterPredicateFormat = this.columnFilterController.filterPredicateFormat();
        if (filterPredicateFormat != null){
            predicateFormat += " AND (" + filterPredicateFormat + ")";
        }

        let fetchRequest = DBHelper.listFetchRequestWithEntityName( "LegalEntityPaymentInstallment", sortDescriptors, predicateFormat );
        fetchRequest.relationshipKeyPathsForPrefetching = [ "paymentMethod", "invoice" , "next", "previous"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, by_date ? "dateDayString" : "dueDateString" );
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    // Collected: paid, or compensated
    private statusPredicateFormat() : string {
        switch (this.statusFilter) {
            case "review": return "status == " + PaymentInstallmentStatus.review;
            case "pending": return "status == " + PaymentInstallmentStatus.pending;
            case "paid": return "(status == " + PaymentInstallmentStatus.paid + " OR status == " + PaymentInstallmentStatus.compensated + ")";
            default: return null;
        }
    }

    filterPredicateDidChange(controller:ColumnFilterController){
        this.invalidateFetch();
    }

    invalidateFetch() {
        this._arranged = {};
        super.invalidateFetch();
    }

    // The rows change in place ( a status, an amount ): keep the scroll
    controllerDidChangeContent(controller:MIOFetchedResultsController) {
        this.reloadTableKeepingScroll();
    }

    private reloadTableKeepingScroll() {
        this._arranged = {};
        let scroll_top = this.tableView.layer.scrollTop;
        this.tableView.reloadData();
        this.tableView.layer.scrollTop = scroll_top;
    }

    // Several sessions load at once: one reload for all of them
    private setNeedsTableReload() {
        if (this.reloadTimer != null) return;
        this.reloadTimer = MIOTimer.scheduledTimerWithTimeInterval( 100, false, this, function(this:PaymentInstallmentViewController, timer:MIOTimer) {
            this.reloadTimer = null;
            this.reloadTableKeepingScroll();
        });
    }

    //
    // The issues of the sessions held for review
    //

    private issuesOfInstallment( item:PaymentInstallment ) : PaymentSessionIssues {
        if (!InstallmentCell.isReviewable( item )) return null;

        let session_id = ( item as AdyenProxyPaymentInstallment ).sessionID;
        let issues = this.sessionIssues[ session_id ];
        if (issues === undefined) this.loadSessionIssues( session_id );
        return issues ?? null;
    }

    private loadSessionIssues( sessionID:string ) {
        if (this.loadingSessions[ sessionID ] == true) return;
        this.loadingSessions[ sessionID ] = true;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.sessionTransactions( sessionID, (data:any, error:string) => {
            delete this.loadingSessions[ sessionID ];
            let result = data?.["data"] ?? data ?? {};
            this.sessionIssues[ sessionID ] = error != null ? null : PaymentSessionIssues.fromEntries( result["transactions"] ?? [] );
            this.setNeedsTableReload();
        });
    }

    private showSessionTransactions( installment:AdyenProxyPaymentInstallment ) {
        let vc = PaymentSessionTransactionsViewController.newInstance();
        vc.delegate = this;
        vc.installment = installment;
        AppHelper.sharedInstance().presentViewController( vc, true );
    }

    //
    // InstallmentCellDelegate
    //

    installmentCellDidRequestReview( cell:InstallmentCell, installment:AdyenProxyPaymentInstallment ) {
        this.showSessionTransactions( installment );
    }

    installmentCellDidChangeSession( cell:InstallmentCell, sessionID:string ) {
        delete this.sessionIssues[ sessionID ];
        // A re-check can split the payout: the pocket is not covered by the
        // session queries, so the list refetches instead of just reloading.
        this.invalidateFetch();
    }

    //
    // PaymentSessionTransactionsViewControllerDelegate
    //

    sessionTransactionsDidGeneratePayout( controller:PaymentSessionTransactionsViewController, sessionID:string ) {
        // The split's pocket is not covered by the session queries: refetch
        this.invalidateFetch();
    }

    sessionTransactionsDidChangeIssues( controller:PaymentSessionTransactionsViewController, sessionID:string, issues:PaymentSessionIssues ) {
        this.sessionIssues[ sessionID ] = issues;
        this.setNeedsTableReload();
    }

    private showOptions() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle( MIOLocalizeString( "OPTIONS", "OPTIONS" ), MIOLocalizeString( "SELECT_AN_OPTION", "SELECT AN OPTION" ), MUIAlertViewStyle.Default );

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "GENERATE_SEPA_FILE", "GENERATE SEPA FILE" ), MUIAlertActionStyle.Default, this, function(this:PaymentInstallmentViewController){
            this.showGenerateSEPAFileViewController();
        } ));

        avc.addAction( MUIAlertAction.alertActionWithTitle( MIOLocalizeString( "CANCEL", "CANCEL" ), MUIAlertActionStyle.Cancel, null, null ) );

        AppHelper.sharedInstance().presentViewController( avc, true );
    }

    private showGenerateSEPAFileViewController() {
        let vc = PaymentInstallmentsSelectionViewController.newInstance();
        vc.setDateRange( this.beginDateString, this.endDateString );

        this.presentViewController( vc, true );
    }

}